"use client"

import { useTranslations } from "next-intl"

import { signout } from "@lib/data/customer"
import { signOutBaSession } from "@lib/util/ba-sign-out"
import { Link } from "@i18n/navigation"
import { LogInIcon as LoginIcon } from "@/components/ui/login"
import { LogoutIcon } from "@/components/ui/logout"
import { UserIcon } from "@/components/ui/user"

type Props = {
  loggedIn: boolean
}

/**
 * Reflects the storefront session in the header. Whether a customer is signed
 * in is decided on the server (the auth cookie is httpOnly) and passed down as
 * a boolean, so this only switches the affordance: a "Sign in" link when
 * logged out, the account link plus a logout action when logged in. Logging
 * out first signs the Better Auth session out on the identity origin
 * (best-effort, never blocks), then runs the same server action the account
 * nav uses, which drops the JWT cookie (0.8.4 计划 工单 11).
 */
const NavAuth = ({ loggedIn }: Props) => {
  const t = useTranslations("Nav")

  const handleLogout = async () => {
    await signOutBaSession()
    await signout()
  }

  if (!loggedIn) {
    return (
      <Link
        href="/account"
        aria-label={t("signIn")}
        className="txt-compact-medium text-ui-fg-subtle hover:text-ui-fg-base transition-colors inline-flex items-center gap-x-1.5"
        data-testid="nav-login-link"
      >
        <LoginIcon size={16} className="shrink-0" />
        {/* 2026-10-25（P0-2）：<414px 顶栏只剩图标，文字进 `aria-label`。
            320px 时右侧「地区 + 账户 + 退出登录」带文字要 204px，占去视口的
            64%，品牌必然被压。图标 + `aria-label` 的可用性不减。 */}
        <span className="hidden min-[414px]:inline">{t("signIn")}</span>
      </Link>
    )
  }

  return (
    <div className="flex shrink-0 items-center gap-x-2 min-[414px]:gap-x-4">
      <Link
        href="/account"
        aria-label={t("account")}
        className="txt-compact-medium text-ui-fg-subtle hover:text-ui-fg-base transition-colors inline-flex items-center gap-x-1.5"
        data-testid="nav-account-link"
      >
        <UserIcon size={16} className="shrink-0" />
        <span className="hidden min-[414px]:inline">{t("account")}</span>
      </Link>
      {/* A click handler rather than a form action: the BA sign-out must fire
          (and settle) before the storefront server action runs. The inline
          button keeps the logout on the same baseline as the sibling links
          (feedback #5). */}
      <button
        type="button"
        onClick={handleLogout}
        className="txt-compact-medium text-ui-fg-subtle hover:text-ui-fg-base transition-colors inline-flex items-center gap-x-1.5"
        data-testid="nav-logout-link"
      >
        <LogoutIcon size={16} className="shrink-0" />
        <span className="hidden min-[414px]:inline">{t("logOut")}</span>
      </button>
    </div>
  )
}

export default NavAuth
